import { useMemo, useEffect, useState } from 'react'
import { addDays, format } from 'date-fns'
import NiceModal, { useModal } from '@ebay/nice-modal-react'

import { Modal, useUtils } from '@pepper/ui'

import { getAssignmentPayload } from '@pepper/utils/src/function/verticalMap'
import getQuotation from '@pepper/utils/src/api/projects/getQuotation'

import styles from './_.module.css'

type Props = {
  onSubmit: () => void
  draftData: any
  assignmentsList: AssignmentListFormValues[]
  accountType: ClientAccountType
  companyId: string
  rootVertical: any
  verticalBasedProcessData: any
  createProjectJSONVersion: {
    id: number
    version: string
  }[]
}

const StandardConfirmOrderModal = NiceModal.create<Props>(
  ({
    onSubmit,
    draftData,
    assignmentsList,
    accountType,
    companyId,
    rootVertical,
    verticalBasedProcessData,
    createProjectJSONVersion,
  }) => {
    const modal = useModal()
    const utilsDataSet = useUtils()
    const [isLoading, setIsLoading] = useState(false)
    const submitModal = () => {
      setIsLoading(true)
      onSubmit()
    }
    const [draftDate, setDraftDate] = useState('')
    const [deliveryDate, setDeliveryDate] = useState('')
    const [priceEstimation, setPriceEstimation] = useState<SmeQuotation | null>(
      null
    )

    useEffect(() => {
      const fetchProjectEstimate = async () => {
        const payload = getAssignmentPayload({
          assignmentsList: draftData?.assignments,
          companyId,
          createProjectJSONVersion,
          genre_id: draftData.genre || draftData.genre_id,
          translation_languages_target_id:
            draftData?.translation_languages_target_id,
          rootCategoryID: rootVertical?.category.id,
          rootExperienceLevel: draftData.experience_level,
          rootVertical,
          selectedPromoCode: '',
          verticalBasedProcessData,
          content_website_page: draftData.content_website_page,
        }) as any
        const { data, status } = await getQuotation(payload)

        if (status === 'ok' && data !== null) {
          setPriceEstimation(data)
        }
      }

      if (modal.visible) fetchProjectEstimate()
    }, [
      accountType,
      assignmentsList,
      companyId,
      createProjectJSONVersion,
      draftData,
      modal.visible,
      rootVertical,
      verticalBasedProcessData,
    ])

    // Calculate draft and delivery dates
    useEffect(() => {
      if (priceEstimation?.first_delivery_estimate) {
        const draftDate = addDays(
          new Date(),
          priceEstimation?.first_delivery_estimate
        )

        const formattedDraftDate = format(draftDate, 'dd-MM-yyyy')

        setDraftDate(formattedDraftDate)
      }

      if (priceEstimation?.project_delivery_estimate) {
        const draftDate = addDays(
          new Date(),
          priceEstimation?.project_delivery_estimate
        )

        const formattedDraftDate = format(draftDate, 'dd-MM-yyyy')

        setDeliveryDate(formattedDraftDate)
      }
    }, [priceEstimation])

    const selectedVertical = useMemo(() => {
      const verticalSelectedSet: {
        vertical: number
        count: number
      }[] = []
      priceEstimation?.includes?.forEach(priceItem => {
        const { vertical } = priceItem
        const matchedIndex = verticalSelectedSet.findIndex(
          selectedItem => selectedItem?.vertical === vertical
        )
        if (matchedIndex > -1 && verticalSelectedSet?.[matchedIndex]?.count) {
          verticalSelectedSet[matchedIndex].count =
            verticalSelectedSet[matchedIndex].count + 1
        } else {
          verticalSelectedSet.push({
            vertical,
            count: 1,
          })
        }
      })
      return verticalSelectedSet?.length > 0
        ? verticalSelectedSet
            ?.map(verticalItem => {
              return `${
                utilsDataSet.verticals.find(
                  vertical => vertical.id === verticalItem.vertical
                )?.title
              } assignments x${verticalItem.count}`
            })
            ?.join(', ')
        : '--'
    }, [utilsDataSet.verticals, priceEstimation])

    const orderSummary: { title: string; value: string }[] = [
      {
        title: 'Total Assignments:',
        value: `${assignmentsList.length} ${selectedVertical}`,
      },
      {
        title: 'First Draft Delivery By:',
        value: draftDate,
      },
      {
        title: 'Complete Delivery By:',
        value: deliveryDate,
      },
    ]

    return (
      <div>
        <Modal
          closable={true}
          visible={modal.visible}
          okButtonProps={{ loading: isLoading }}
          onOk={async () => {
            await submitModal()
            modal.remove()
          }}
          onCancel={() => {
            modal.remove()
          }}
          okText="Confirm Order"
          title="Sure you want to complete the order?"
        >
          <p className={styles.orderDescription}>
            By completing the order, you are confirming to the following -{' '}
          </p>
          <div className={styles.orderSummaryContainer}>
            {orderSummary.map((summary, index) => (
              <div key={index}>
                <p className={styles.title}>{summary.title}</p>
                <p className={styles.value}>{summary.value}</p>
              </div>
            ))}
          </div>
        </Modal>
      </div>
    )
  }
)

export default StandardConfirmOrderModal
